import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../api.js';
import { useMe } from '../auth.js';
import { Button, ErrorText, Input } from '../components/ui.js';
import { BookkeepingBanner, useBookkeepingStatus, type BookkeepingStatus } from './InvoicesPage.js';
import { TAX_MODE_TEXT, type InvoiceTaxMode } from './PartnersPage.js';

interface PostingCategory {
  id: string;
  name: string;
  type: 'income' | 'outgo';
  groupName: string;
}
type PurchaseCategories = BookkeepingStatus['settings']['purchase'];

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.message ?? e.code) : fallback);
const selectClass = 'w-full rounded border border-slate-300 bg-white px-2 py-1.5 dark:border-slate-700 dark:bg-slate-900';
/** Lexware's "Dienstleistungen an Drittländer": Valve (US) buys a B2B service, not taxable in Germany. */
const DEFAULT_SALES_CATEGORY_ID = 'ef5b1a6e-f690-4004-9a19-91276348894f';

function CategorySelect({ label, type, value, categories, onChange }: { label: string; type: 'income' | 'outgo'; value: string; categories: PostingCategory[]; onChange: (id: string) => void }) {
  const options = categories.filter((c) => c.type === type);
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-slate-600 dark:text-slate-400">{label}</span>
      <select aria-label={label} className={selectClass} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">choose...</option>
        {value && !options.some((c) => c.id === value) && <option value={value}>{value === DEFAULT_SALES_CATEGORY_ID ? 'Dienstleistungen an Drittländer (default, not in this account)' : `${value} (not in this account)`}</option>}
        {options.map((c) => <option key={c.id} value={c.id}>{c.groupName}: {c.name}</option>)}
      </select>
    </label>
  );
}

/**
 * Lexware Office settings (STE-11): the API key (tested against Lexware, stored encrypted; only its last four
 * characters are ever shown), the posting category of the Steam payment and one per partner invoice VAT mode, the
 * first payout booked, the reminder period for missing partner invoices and the retention of unmatched mail.
 */
export function BookkeepingSettings() {
  const isAdmin = useMe().user?.role === 'admin';
  const client = useQueryClient();
  const status = useBookkeepingStatus();
  const s = status.data;
  const [key, setKey] = useState('');
  const [sales, setSales] = useState<string | null>(null);
  const [purchase, setPurchase] = useState<PurchaseCategories | null>(null);
  const [retentionDays, setRetentionDays] = useState<string | null>(null);
  const [bookFrom, setBookFrom] = useState<string | null>(null);
  const [reminderDays, setReminderDays] = useState<string | null>(null);
  const categories = useQuery({
    queryKey: ['invoices', 'posting-categories', s?.key?.hint],
    queryFn: () => api<PostingCategory[]>('GET', '/api/bookkeeping/posting-categories'),
    enabled: isAdmin && Boolean(s?.key),
    retry: false,
  });
  const refresh = () => client.invalidateQueries({ queryKey: ['invoices'] });
  const saveKey = useMutation({ mutationFn: () => api<{ hint: string; companyName: string }>('PUT', '/api/bookkeeping/key', { key }), onSuccess: async () => { setKey(''); await refresh(); } });
  const removeKey = useMutation({ mutationFn: () => api('DELETE', '/api/bookkeeping/key'), onSuccess: refresh });
  const save = useMutation({
    mutationFn: () =>
      api('PUT', '/api/bookkeeping/settings', {
        ...(sales ? { sales: { categoryId: sales } } : {}),
        ...(purchase ? { purchase } : {}),
        ...(retentionDays !== null ? { retentionDays: Number(retentionDays) } : {}),
        ...(bookFrom !== null ? { bookFrom: bookFrom || null } : {}),
        ...(reminderDays !== null ? { reminderDays: Number(reminderDays) } : {}),
      }),
    onSuccess: async () => {
      setSales(null);
      setPurchase(null);
      setBookFrom(null);
      setReminderDays(null);
      setRetentionDays(null);
      await refresh();
    },
  });
  if (!s) return status.isError ? <ErrorText>{errorText(status.error, 'The bookkeeping settings could not be loaded')}</ErrorText> : null;
  const dirty = sales !== null || purchase !== null || bookFrom !== null || reminderDays !== null || retentionDays !== null;
  const purchaseValue = { ...s.settings.purchase, ...purchase };
  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold">Bookkeeping (Lexware Office)</h2>
      <BookkeepingBanner status={s} />
      <dl className="grid max-w-2xl grid-cols-[14rem_1fr] gap-y-1 text-sm">
        <dt className="text-slate-500">API key</dt>
        <dd>{s.key ? `${s.key.hint} (set ${new Date(s.key.updatedAt).toLocaleString('en-GB')})` : 'not configured'}</dd>
        <dt className="text-slate-500">First payout booked</dt>
        <dd>{s.settings.bookFrom ?? 'not set'}</dd>
        <dt className="text-slate-500">Invoice reminder after</dt>
        <dd>{s.settings.reminderDays} days</dd>
        <dt className="text-slate-500">Retention of unmatched mail</dt>
        <dd>{s.settings.retentionDays} days (dismissed items, unknown senders, unclassified mails)</dd>
        <dt className="text-slate-500">Steam payments</dt>
        <dd>gross, 0 % VAT (Valve, US: not taxable in Germany){s.settings.sales.isDefault ? ', default category Dienstleistungen an Drittländer' : ''}</dd>
      </dl>
      {isAdmin && (
        <>
          <form className="flex items-end gap-2" onSubmit={(e: FormEvent) => { e.preventDefault(); saveKey.mutate(); }}>
            <div className="w-96">
              <Input label={s.key ? 'Rotate: new API key' : 'API key (app.lexware.de/addons/public-api)'} type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value.trim())} />
            </div>
            <Button type="submit" disabled={saveKey.isPending || key.length === 0}>Test and save</Button>
            {s.key && <Button type="button" className="bg-red-700 hover:bg-red-600 dark:bg-red-300" onClick={() => removeKey.mutate()} disabled={removeKey.isPending}>Remove key</Button>}
          </form>
          {saveKey.data && <p className="text-sm text-emerald-700 dark:text-emerald-400">Key {saveKey.data.hint} works for {saveKey.data.companyName}.</p>}
          <form className="max-w-2xl space-y-3" onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }}>
            {s.key && categories.data && (
              <>
                <CategorySelect label="Steam payments (salesinvoice, 0 % VAT): posting category" type="income" categories={categories.data} value={sales ?? s.settings.sales.categoryId} onChange={setSales} />
                <fieldset className="space-y-2 rounded border border-slate-200 p-3 dark:border-slate-800">
                  <legend className="px-1 text-sm font-medium">Partner invoices (purchaseinvoice, gross): posting category per invoice VAT of the partner</legend>
                  {(Object.keys(TAX_MODE_TEXT) as InvoiceTaxMode[]).map((mode) => (
                    <CategorySelect
                      key={mode}
                      label={`${TAX_MODE_TEXT[mode]}${s.settings.modesInUse.includes(mode) ? ' (in use)' : ''}`}
                      type="outgo"
                      categories={categories.data}
                      value={purchaseValue[mode] ?? ''}
                      onChange={(id) => setPurchase({ ...purchase, [mode]: id || undefined })}
                    />
                  ))}
                </fieldset>
              </>
            )}
            {s.key && categories.isError && <ErrorText>{errorText(categories.error, 'The posting categories could not be read from Lexware')}</ErrorText>}
            {!s.key && <p className="text-sm text-slate-500">Posting categories come from Lexware once the key is saved.</p>}
            <Input label="First Steam payout to book (YYYY-MM-DD; earlier payments and their invoices stay out of Lexware)" type="date" value={bookFrom ?? s.settings.bookFrom ?? ''} onChange={(e) => setBookFrom(e.target.value)} />
            <Input label="Remind me of a missing partner invoice after (days)" type="number" min={1} max={365} value={reminderDays ?? String(s.settings.reminderDays)} onChange={(e) => setReminderDays(e.target.value)} />
            <Input label="Delete dismissed items, items of unknown senders and unclassified mails after (days)" type="number" min={7} max={3650} value={retentionDays ?? String(s.settings.retentionDays)} onChange={(e) => setRetentionDays(e.target.value)} />
            <Button type="submit" disabled={save.isPending || !dirty}>Save bookkeeping settings</Button>
          </form>
        </>
      )}
      {(saveKey.error ?? removeKey.error ?? save.error) && <ErrorText>{errorText(saveKey.error ?? removeKey.error ?? save.error, 'The change failed')}</ErrorText>}
    </section>
  );
}
